import React, { useEffect, useState, useMemo } from "react";
import { Autocomplete, TextField, MenuItem } from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";
import { useAuthTagContext } from "../../contexts/TagAndSystemProvider";

/**
 * Interface for auth tag data from API
 */
interface AuthTag {
  id: number;
  name: string;
  is_active?: boolean;
  [key: string]: any;
}

/**
 * Props interface for EditAuthTags component
 */
interface EditAuthTagsProps {
  /** Array of currently selected tag IDs */
  selectedTagIds: number[];
  /** Callback function when tags selection changes */
  onTagsChange: (selectedIds: number[]) => void;
  /** String identifier for the entity type (for toast messages) */
  entityName: string;
  /** Toast function for notifications */
  showToast: (
    message: string,
    type: "success" | "error" | "warning" | "warn"
  ) => void;
  /** Validation errors for the field */
  errors?: string;
  /** Field validation function */
  validateField?: (fieldName: string, value: any) => void;
  /** Optional CSS class for styling customization */
  className?: string;
  /** Field name for validation */
  fieldName?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Custom label for the field */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to use only active tags (default: true) */
  useActiveTagsOnly?: boolean;
  /** Optional loading state callback */
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether the field is disabled */
  disabled?: boolean;
}

/**
 * Reusable component for editing auth tags with multiple selection
 * Handles active/inactive filtering and toast notifications for inactive tags
 */
const EditAuthTags: React.FC<EditAuthTagsProps> = ({
  selectedTagIds,
  onTagsChange,
  entityName,
  showToast,
  errors,
  validateField,
  className = "",
  fieldName = "tags",
  required = true,
  label,
  placeholder = "Select...",
  useActiveTagsOnly = true,
  setIsLoading,
  disabled = false,
}) => {
  const [inactiveTagsNotified, setInactiveTagsNotified] = useState<Set<number>>(
    new Set()
  );

  // Get all auth tags data from context
  const { allAuthTagsData } = useAuthTagContext();

  // Apply filtering to auth tags data based on useActiveTagsOnly setting
  const availableAuthTags = useMemo(() => {
    if (!Array.isArray(allAuthTagsData)) {
      return [];
    }

    // Apply active tag filtering when useActiveTagsOnly is true (default)
    if (useActiveTagsOnly) {
      return allAuthTagsData.filter(
        (tag: AuthTag) => tag.is_active === true || tag.is_active === undefined
      );
    }

    // Return all tags when useActiveTagsOnly is false
    return allAuthTagsData;
  }, [allAuthTagsData, useActiveTagsOnly]);

  // Handle inactive tag notifications
  useEffect(() => {
    if (
      Array.isArray(allAuthTagsData) &&
      Array.isArray(selectedTagIds) &&
      selectedTagIds.length > 0
    ) {
      // Find selected auth tags using unfiltered data to detect inactive tags
      const selectedAuthTags = allAuthTagsData.filter((authTag: AuthTag) =>
        selectedTagIds.includes(authTag.id)
      );

      // Find inactive tags that haven't been notified yet
      const inactiveTags = selectedAuthTags.filter(
        (tag: AuthTag) =>
          tag.is_active === false && !inactiveTagsNotified.has(tag.id)
      );

      // Show toast notifications for new inactive tags
      if (inactiveTags.length > 0) {
        inactiveTags.forEach((tag: AuthTag) => {
          showToast(`Tag ID ${tag.id} is currently inactive`, "error");
        });

        // Update the notified set
        setInactiveTagsNotified((prev) => {
          const newSet = new Set(prev);
          inactiveTags.forEach((tag) => newSet.add(tag.id));
          return newSet;
        });
      }
    }
  }, [
    allAuthTagsData,
    selectedTagIds,
    entityName,
    inactiveTagsNotified,
    showToast,
  ]);

  // Get selected tag objects for display
  const selectedTagObjects = availableAuthTags.filter((authTag: AuthTag) =>
    selectedTagIds.includes(authTag.id)
  );

  const handleTagsChange = (_: React.SyntheticEvent, value: AuthTag[]) => {
    const selectedIds = value.map((item: AuthTag) => item.id);
    onTagsChange(selectedIds);

    if (validateField) {
      validateField(fieldName, selectedIds);
    }
  };

  const displayLabel = label || `Tag${required ? "" : "(s)"}`;

  return (
    <Autocomplete
      fullWidth
      multiple
      disableCloseOnSelect
      disabled={disabled}
      className={`form-control-autocomplete ${className}`}
      options={availableAuthTags}
      getOptionLabel={(option: AuthTag) => option?.name || option?.value || ""}
      isOptionEqualToValue={(opt: AuthTag, val: AuthTag) => opt?.id === val?.id}
      value={selectedTagObjects}
      onChange={handleTagsChange}
      renderInput={(params) => (
        <div className="autocomplete-chips-direction">
          <TextField
            {...params}
            title="Enter Tag"
            placeholder={placeholder}
            label={
              required ? (
                <span>
                  {displayLabel} <span className="required-asterisk">*</span>
                </span>
              ) : (
                displayLabel
              )
            }
            variant="outlined"
            className={`form-control-autocomplete ${errors ? "has-error" : ""}`}
            InputLabelProps={{
              shrink: true,
            }}
            error={!!errors}
            helperText={errors || ""}
          />
        </div>
      )}
      renderOption={(props, option, { selected }) => (
        <MenuItem
          {...props}
          key={`${option?.id}-${option?.name || option?.value}`}
          value={option?.name || option?.value}
          sx={{ justifyContent: "space-between" }}
        >
          {option?.name || option?.value}
          {selected ? <CheckCircleRounded color="info" /> : null}
        </MenuItem>
      )}
      loadingText="Loading..."
    />
  );
};

export default EditAuthTags;
